import React, { useMemo } from "react";
import { Card, ChartSkeleton, Delta, Empty, Info, Kpi, Pill, StatusPill, Table } from "../ui";
import { BarList, DonutBreakdown, StackedBars } from "../charts";
import { Icon } from "../icons";
import { dayFilter, useFilters } from "../filters";
import { capabilityLabel, useView, workspaceLabel } from "../data";
import { ENTITY_LABELS, FRICTION_LABELS, compact, fmt, ms, pct, statusOf, usd, whenShort, words } from "../format";
import { RUN_PARTS, fillDays, seriesByDay } from "./common";

/**
 * The first view: the headline numbers with their change against the
 * previous period, requests over time (click a segment to open those
 * conversations), a health scorecard, and the breakdowns that explain them.
 */
export default function Overview({ overview: o, accounts }) {
  const { params, prevParams, compare, window: w, set } = useFilters();
  const prev = useView("overview", prevParams, null, { skip: !compare }).data;
  const series = useView("timeseries", params);
  const days = useMemo(() => fillDays(series.data?.daily, w.from, w.to), [series.data, w.from, w.to]);
  const creditsByDay = useMemo(() => seriesByDay(series.data?.credits), [series.data]);
  const frictionByDay = useMemo(() => seriesByDay(series.data?.friction), [series.data]);

  const loading = !o;
  const d = (cur, before, opts) => (compare && prev ? <Delta cur={cur} prev={before} {...opts} /> : null);
  const by = o?.runs?.byStatus ?? {};
  const open = (by.suspended ?? 0) + (by.paused ?? 0) + (by.running ?? 0) + (by.pending ?? 0);
  const turns = o?.turns ?? 0;
  const fb = o?.feedback ?? {};
  const positive = (fb.thumbsUp ?? 0) + (fb.sessions?.good ?? 0);
  const negative = (fb.thumbsDown ?? 0) + (fb.sessions?.bad ?? 0);
  const rated = positive + negative;
  const frictionPerTurn = turns ? (o?.friction?.total ?? 0) / turns : null;
  const prevTurns = prev?.turns ?? 0;
  const hasCost = Boolean(o?.cost?.models?.length);
  const drill = (patch) => set({ tab: "conversations", ...patch });

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {/* Headline numbers */}
      <div className="aa-grid aa-grid-kpi">
        <Kpi
          loading={loading}
          label="Requests"
          info="A request is one thing a user asked the agent to do. A conversation can hold several."
          value={fmt(o?.runs?.total)}
          delta={d(o?.runs?.total, prev?.runs?.total)}
          foot={`${fmt(open)} open`}
          spark={days.map((x) => x.runs)}
          onClick={() => drill({})}
        />
        <Kpi
          loading={loading}
          label="Active people"
          value={fmt(o?.usage?.users)}
          delta={d(o?.usage?.users, prev?.usage?.users)}
          foot={`${fmt(o?.usage?.returningUsers)} returning · ${fmt(o?.usage?.accounts)} workspaces`}
          spark={days.map((x) => x.users)}
          sparkColor="var(--aa-s3)"
          onClick={() => set({ tab: "users" })}
        />
        <Kpi
          loading={loading}
          label="Success rate"
          info="Of requests that reached an end, the share that finished. One still waiting on the user's answer is not counted as a failure."
          value={pct(o?.runs?.successRate)}
          status={statusOf(o?.runs?.successRate, { good: 0.9, bad: 0.75 })}
          delta={d(o?.runs?.successRate, prev?.runs?.successRate, { kind: "rate" })}
          foot={`${fmt(by.failed ?? 0)} failed`}
          spark={days.map((x) => x.successRate ?? 0)}
          sparkColor="var(--aa-good)"
          onClick={() => drill({ status: "failed" })}
        />
        <Kpi
          loading={loading}
          label="Did what was asked"
          info="An AI reviewer reads each turn after it ends and scores whether it did what the user asked (a partial counts half)."
          value={pct(o?.taskOutcome?.average)}
          status={statusOf(o?.taskOutcome?.average, { good: 0.85, bad: 0.6 })}
          delta={d(o?.taskOutcome?.average, prev?.taskOutcome?.average, { kind: "rate" })}
          foot={`${fmt(o?.taskOutcome?.judgedTurns)} turns reviewed`}
          onClick={() => set({ tab: "quality" })}
        />
        <Kpi
          loading={loading}
          label="Avg response time"
          info="From a message to the agent's reply or question — never the time it then waits on the user. Under 10s is good, over 30s a problem."
          value={ms(o?.responseTime?.avgMs)}
          status={statusOf(o?.responseTime?.avgMs, { good: 10_000, bad: 30_000, higherIsBetter: false })}
          delta={d(o?.responseTime?.avgMs, prev?.responseTime?.avgMs, { better: "down" })}
          foot={o?.responseTime?.maxMs ? `slowest ${ms(o.responseTime.maxMs)}` : null}
          onClick={() => set({ tab: "latency" })}
        />
        <Kpi
          loading={loading}
          label="Stuck moments"
          info="Repeated questions, replies the agent could not read, empty searches, errors and credit blocks."
          value={fmt(o?.friction?.total)}
          status={statusOf(frictionPerTurn, { good: 0.1, bad: 0.3, higherIsBetter: false })}
          delta={d(frictionPerTurn, prevTurns ? (prev?.friction?.total ?? 0) / prevTurns : null, { better: "down" })}
          foot={frictionPerTurn == null ? "nothing recorded" : `${fmt(frictionPerTurn, 2)} per turn`}
          spark={days.map((x) => frictionByDay.get(x.key) ?? 0)}
          sparkColor="var(--aa-s2)"
        />
        <Kpi
          loading={loading}
          label="Credits charged"
          info="Charged to customers by the capabilities they used."
          value={fmt(o?.credits?.total)}
          delta={d(o?.credits?.total, prev?.credits?.total, { better: null })}
          foot={`${compact(o?.recordsSaved)} records saved`}
          spark={days.map((x) => creditsByDay.get(x.key) ?? 0)}
          sparkColor="var(--aa-s4)"
        />
        <Kpi
          loading={loading}
          label="AI cost"
          info="Model cost as the AI gateway reported it (list price where it reports none)."
          value={hasCost ? usd(o.cost.totalUsd) : "—"}
          delta={hasCost && prev?.cost?.models?.length ? d(o.cost.totalUsd, prev.cost.totalUsd, { better: "down" }) : null}
          foot={hasCost && turns ? `${usd(o.cost.totalUsd / turns)} per turn` : "no AI usage recorded"}
        />
      </div>

      {/* Requests over time + scorecard */}
      <div className="aa-grid aa-grid-main">
        <Card
          title="Requests over time"
          subtitle="Daily requests by how they ended. Click a segment to open those conversations."
          actions={<Pill tone="neutral">{fmt(o?.runs?.total)} total</Pill>}
        >
          {series.loading ? (
            <ChartSkeleton h={290} />
          ) : days.some((x) => x.runs) ? (
            <StackedBars
              rows={days}
              series={RUN_PARTS}
              height={260}
              onSelect={(row, key) => drill({ ...dayFilter(row.key), status: RUN_PARTS.find((p) => p.key === key)?.status })}
              footer={(row) => (
                <div className="aa-tip-row" style={{ color: "var(--aa-muted)" }}>
                  People
                  <b>{fmt(row.users)}</b>
                </div>
              )}
            />
          ) : (
            <Empty icon="activity" title="No requests in this period">
              Try a longer date range, or another workspace.
            </Empty>
          )}
        </Card>

        <Card title="Health scorecard" subtitle={turns ? `Based on ${fmt(turns)} recorded turns` : "Nothing recorded in this period yet"} flush>
          <Health
            icon="checkCircle"
            label="Requests finish"
            value={pct(o?.runs?.successRate)}
            status={statusOf(o?.runs?.successRate, { good: 0.9, bad: 0.75 })}
            help="Good at 90%+, a problem under 75%."
          />
          <Health
            icon="quality"
            label="Does what people ask"
            value={pct(o?.taskOutcome?.average)}
            status={statusOf(o?.taskOutcome?.average, { good: 0.85, bad: 0.6 })}
            help="AI-reviewed. Good at 85%+, a problem under 60%."
          />
          <Health
            icon="activity"
            label="Runs without errors"
            value={pct(o?.flowHealth)}
            status={statusOf(o?.flowHealth, { good: 0.95, bad: 0.85 })}
            help="Turns that finished, or correctly waited for the user."
          />
          <Health
            icon="stuck"
            label="Rarely gets stuck"
            value={frictionPerTurn == null ? "—" : `${fmt(frictionPerTurn, 2)}/turn`}
            status={statusOf(frictionPerTurn, { good: 0.1, bad: 0.3, higherIsBetter: false })}
            help="Stuck moments per turn. 0 is best."
          />
          <Health
            icon="clock"
            label="Answers quickly"
            value={ms(o?.responseTime?.avgMs)}
            status={statusOf(o?.responseTime?.avgMs, { good: 10_000, bad: 30_000, higherIsBetter: false })}
            help="Average wait for a reply."
          />
          <Health
            icon="smile"
            label="People are happy"
            value={rated ? pct(positive / rated) : "—"}
            status={rated ? statusOf(positive / rated, { good: 0.8, bad: 0.6 }) : "none"}
            help={rated ? `${fmt(positive)} positive · ${fmt(negative)} negative ratings` : "No ratings yet."}
          />
        </Card>
      </div>

      {/* Breakdowns */}
      <div className="aa-grid aa-grid-3">
        <Card title="How requests ended" subtitle="Click a status to open those conversations">
          {o?.runs?.total ? (
            <DonutBreakdown
              center={pct(o.runs.successRate)}
              sub="success"
              segments={[
                { label: "Finished", value: by.success ?? 0, color: "var(--aa-good)", onClick: () => drill({ status: "finished" }) },
                { label: "Still open", value: open, color: "var(--aa-neutral)", help: "Mostly waiting on the user", onClick: () => drill({ status: "open" }) },
                { label: "Stopped by user", value: by.canceled ?? 0, color: "var(--aa-warn)", onClick: () => drill({ status: "stopped" }) },
                { label: "Failed", value: by.failed ?? 0, color: "var(--aa-bad)", onClick: () => drill({ status: "failed" }) },
              ]}
            />
          ) : (
            <Empty compact title="No requests yet" />
          )}
        </Card>

        <Card title="Where people got stuck" subtitle="Stuck moments by kind">
          {Object.keys(o?.friction?.byKind ?? {}).length ? (
            <BarList
              color="var(--aa-s2)"
              rows={Object.entries(o.friction.byKind)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => ({ key: k, label: FRICTION_LABELS[k] ?? words(k), value: v }))}
            />
          ) : (
            <Empty compact icon="checkCircle" title={turns ? "No one got stuck" : "Nothing recorded yet"} />
          )}
        </Card>

        <Card title="Credits by capability" subtitle="What customers were charged for">
          {Object.keys(o?.credits?.byCapability ?? {}).length || Object.keys(o?.credits?.byOperation ?? {}).length ? (
            <BarList
              color="var(--aa-s4)"
              limit={6}
              rows={Object.entries(Object.keys(o.credits.byCapability ?? {}).length ? o.credits.byCapability : o.credits.byOperation)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => ({ key: k, label: capabilityLabel(k), value: v }))}
            />
          ) : (
            <Empty compact icon="credit" title="No credits charged" />
          )}
        </Card>
      </div>

      <CreatedCard params={params} set={set} />

      <div className="aa-grid aa-grid-2">
        <Card title="Top workspaces" subtitle="By requests · click one to filter the page to it">
          {accounts?.accounts?.length ? (
            <BarList
              limit={8}
              labelHeader="Workspace"
              valueLabel="Requests"
              rows={[...accounts.accounts]
                .sort((a, b) => b.runs - a.runs)
                .map((a) => ({
                  key: a.accountId,
                  label: workspaceLabel(a.accountId),
                  value: a.runs,
                  note: a.successRate == null ? null : pct(a.successRate),
                  title: `${workspaceLabel(a.accountId)} — ${fmt(a.runs)} requests, ${pct(a.successRate)} success`,
                  onClick: () => set({ ws: a.accountId }),
                }))}
            />
          ) : (
            <Empty compact icon="workspaces" title="No workspace activity" />
          )}
        </Card>

        <Card title="AI cost by model" subtitle="As the AI gateway reported it" flush>
          {hasCost ? (
            <Table
              defaultSort={{ key: "usd", dir: "desc" }}
              columns={[
                { key: "model", label: "Model", render: (r) => <span className="aa-mono">{r.model === "unknown" ? "Not named (older records)" : r.model}</span> },
                {
                  key: "inputTokens",
                  label: "Tokens in",
                  align: "right",
                  render: (r) => (
                    <span title={`${fmt(r.cacheReadTokens)} reused from cache`}>
                      {compact(r.inputTokens)}
                      {r.inputTokens > 0 && r.cacheReadTokens > 0 && <span className="aa-sub">{pct(r.cacheReadTokens / r.inputTokens)} cached</span>}
                    </span>
                  ),
                },
                { key: "outputTokens", label: "Tokens out", align: "right", render: (r) => compact(r.outputTokens) },
                { key: "usd", label: "Cost", align: "right", render: (r) => (r.usd == null ? <span style={{ color: "var(--aa-faint)" }}>not reported</span> : usd(r.usd)) },
              ]}
              rows={o.cost.models.map((m) => ({ ...m, id: m.model }))}
            />
          ) : (
            <Empty compact icon="dollar" title="No AI usage recorded" />
          )}
        </Card>
      </div>
    </div>
  );
}

const TONE_BG = { good: "var(--aa-good-soft)", watch: "var(--aa-warn-soft)", bad: "var(--aa-bad-soft)", none: "var(--aa-surface-3)" };
const TONE_INK = { good: "var(--aa-good-ink)", watch: "var(--aa-warn-ink)", bad: "var(--aa-bad-ink)", none: "var(--aa-muted)" };

function Health({ icon, label, value, status, help }) {
  return (
    <div className="aa-health-row">
      <span className="aa-icon-tile" style={{ background: TONE_BG[status], color: TONE_INK[status] }}>
        <Icon name={icon} size={15} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
          {label}
          <Info text={help} />
        </div>
        <div style={{ marginTop: 3 }}>
          <StatusPill status={status} />
        </div>
      </div>
      <div className="aa-num" style={{ fontSize: 18, fontWeight: 600, color: TONE_INK[status] === "var(--aa-muted)" ? "var(--aa-ink)" : TONE_INK[status] }}>
        {value}
      </div>
    </div>
  );
}

/**
 * What conversations created in HP in this period — segments, workflows,
 * enrichment tasks, sources, jobs — by kind (a click opens the conversations
 * that created one), and the newest of them (a click opens that conversation).
 */
function CreatedCard({ params, set }) {
  const { data, loading } = useView("entities", params);
  const byKind = data?.byKind ?? [];
  const recent = (data?.recent ?? []).slice(0, 8);
  const openKind = (kind) => set({ tab: "conversations", "c.created": kind });
  return (
    <Card title="What the agent created" subtitle="Things made in HP during these conversations · click a kind to see the conversations, a row to open one">
      {loading ? (
        <ChartSkeleton h={160} />
      ) : byKind.length ? (
        <div className="aa-grid aa-grid-2" style={{ alignItems: "start" }}>
          <BarList
            color="var(--aa-s1)"
            valueLabel="Created"
            rows={byKind.map((k) => ({
              key: k.kind,
              label: ENTITY_LABELS[k.kind]?.label ?? words(k.kind),
              value: k.created,
              note: `${fmt(k.conversations)} conv.`,
              onClick: () => openKind(k.kind),
            }))}
          />
          <div className="aa-card" style={{ boxShadow: "none", overflow: "hidden" }}>
            <Table
              columns={[
                { key: "first_seen_at", label: "When", nowrap: true, render: (r) => whenShort(r.first_seen_at) },
                { key: "kind", label: "What", render: (r) => <Pill tone="neutral">{ENTITY_LABELS[r.kind]?.one ?? words(r.kind)}</Pill> },
                { key: "label", label: "Name", max: 200, render: (r) => r.label || <span className="aa-mono" style={{ fontSize: 12 }}>{r.entity_id}</span> },
                { key: "title", label: "Conversation", max: 220, render: (r) => <span style={{ color: "var(--aa-ink-2)" }}>{r.title || r.first_prompt || "—"}</span> },
              ]}
              rows={recent.map((r, i) => ({ ...r, id: `${r.kind}-${r.entity_id}-${i}` }))}
              onRow={(r) => set({ session: r.conversation_id })}
            />
          </div>
        </div>
      ) : (
        <Empty compact title="Nothing created in this period" />
      )}
    </Card>
  );
}
